import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveDynamicComponent as _resolveDynamicComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, Teleport as _Teleport, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, normalizeClass as _normalizeClass, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("strong", { class: "text-xl leading-8 block font-normal text-gray-500 mt-[1.3333333em]" }, "Sender Policy Framework (SPF) records in the DNS identify the mail servers allowed to send email for a domain. SPF protects domains and their brands from email abuse by spammers and hackers.", -1)
const _hoisted_2 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("The Simple Mail Transfer Protocol (SMTP) is used to send email. SMTP does not include validation that the sending mail server is allowed to send mail for a domain. So, anyone can use SMTP to send email that appears to be from any domain they wish. For example, a hacker might try to send phishing emails to Bank of America customers from "),
  /*#__PURE__*/_createElementVNode("code", null, "accounts@bankofamerica.com"),
  /*#__PURE__*/_createTextVNode(" to gain access to their bank accounts.")
], -1)
const _hoisted_3 = /*#__PURE__*/_createElementVNode("p", null, "SPF addresses this weakness in SMTP. It identifies the mail servers permitted to send email for the domain. Email from a server not permitted by the SPF record will be discarded, quarantined, or placed in a spam folder.", -1)
const _hoisted_4 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("SPF is specified in "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc7208",
    rel: "noopener noreferrer"
  }, "RFC 7208"),
  /*#__PURE__*/_createTextVNode(".")
], -1)
const _hoisted_5 = /*#__PURE__*/_createElementVNode("figure", { class: "mt-6" }, [
  /*#__PURE__*/_createElementVNode("img", {
    class: "w-full rounded-lg",
    src: "/img/learning/spf-a-practical-guide/the-dns-spf-record-type.jpg",
    alt: "Only mail servers with the correct identity can deliver email",
    title: "Only mail servers with the correct identity can deliver email",
    width: "1200",
    height: "600"
  }),
  /*#__PURE__*/_createElementVNode("figcaption", null, [
    /*#__PURE__*/_createTextVNode("Only mail servers with the correct identity can deliver email. By NsLookup.io. Licensed under "),
    /*#__PURE__*/_createElementVNode("a", {
      href: "https://creativecommons.org/licenses/by/4.0/",
      title: "Creative Commons By 4.0 licence",
      rel: "nofollow"
    }, "CC By 4.0"),
    /*#__PURE__*/_createTextVNode(".")
  ])
], -1)
const _hoisted_6 = /*#__PURE__*/_createElementVNode("h2", null, "How SPF works", -1)
const _hoisted_7 = /*#__PURE__*/_createElementVNode("p", null, "The receiving mail server extracts the domain from the Return-Path field as it receives each email message. A DNS TXT lookup is performed on this domain to retrieve the domain's SPF record. The source server of the email must be among the mail servers identified by the SPF record.", -1)
const _hoisted_8 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("The most straightforward way to identify a mail server in SPF is by IP address. Below is an SPF record that allows the mail server with IP address 10.0.0.1 to send email for the "),
  /*#__PURE__*/_createElementVNode("code", null, "example.org"),
  /*#__PURE__*/_createTextVNode(" domain. No other servers are permitted to send email for the domain.")
], -1)
const _hoisted_9 = /*#__PURE__*/_createElementVNode("pre", null, "example.org. 3600 TXT \"v=spf1 ip4:10.0.0.1 -all\"", -1)
const _hoisted_10 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "Email headers contain several \"from\" fields that each serve a different purpose. SPF uses the \"Return-Path\" field during validation.")
], -1)
const _hoisted_11 = /*#__PURE__*/_createElementVNode("p", null, "Return-Path is the email address where bounce messages are sent. Mail servers use the domain portion of Return-Path to find the domain's SPF record. However, email clients usually display the From field to the user. The From field may contain a different domain.", -1)
const _hoisted_12 = /*#__PURE__*/_createElementVNode("p", null, "This limitation of SPF is mitigated in two ways:", -1)
const _hoisted_13 = /*#__PURE__*/_createElementVNode("ul", null, [
  /*#__PURE__*/_createElementVNode("li", null, "Domain-based Message Authentication, Reporting, and Conformance (DMARC) adds a domain alignment policy. This policy requires that the From and Return-Path domains match."),
  /*#__PURE__*/_createElementVNode("li", null, "Modern email clients often flag messages where the From and Return-Path domains are different as unsafe.")
], -1)
const _hoisted_14 = /*#__PURE__*/_createElementVNode("h2", null, "SPF record format", -1)
const _hoisted_15 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("Every SPF record must begin with "),
  /*#__PURE__*/_createElementVNode("code", null, "v=spf1"),
  /*#__PURE__*/_createTextVNode(". This specifies a version 1 SPF record. This is the only version so far.")
], -1)
const _hoisted_16 = /*#__PURE__*/_createElementVNode("p", null, "Next comes one or more directives. Each directive consists of an optional qualifier followed by a mechanism. The qualifier specifies if mail servers matching this mechanism pass or fail. Each mechanism identifies mail servers by IP address or through extra DNS lookups.", -1)
const _hoisted_17 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("The SPF record ends with the mechanism "),
  /*#__PURE__*/_createElementVNode("code", null, "all"),
  /*#__PURE__*/_createTextVNode(". The "),
  /*#__PURE__*/_createElementVNode("code", null, "all"),
  /*#__PURE__*/_createTextVNode(" mechanism is used if no other mechanism is a match.")
], -1)
const _hoisted_18 = /*#__PURE__*/_createElementVNode("p", null, "The receiving mail server processes the SPF record from left to right. SPF processing ends when the first match is found. The first match determines if the email will be accepted, rejected, or treated as suspicious.", -1)
const _hoisted_19 = /*#__PURE__*/_createElementVNode("h2", null, "SPF qualifiers", -1)
const _hoisted_20 = /*#__PURE__*/_createElementVNode("p", null, "Each directive has one of four possible qualifiers:", -1)
const _hoisted_21 = /*#__PURE__*/_createElementVNode("ul", null, [
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("strong", null, "\"+\""),
    /*#__PURE__*/_createTextVNode(" Pass. The source server is allowed to send email for the domain. If there is no qualifier, "),
    /*#__PURE__*/_createElementVNode("code", null, "+"),
    /*#__PURE__*/_createTextVNode(" is assumed.")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("strong", null, "\"-\""),
    /*#__PURE__*/_createTextVNode(" Fail. The source server is not allowed to send email for the domain.")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("strong", null, "\"~\""),
    /*#__PURE__*/_createTextVNode(" Softfail. This is a weak failure. The source server is probably not allowed to send email for the domain.")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("strong", null, "\"?\""),
    /*#__PURE__*/_createTextVNode(" Neutral. The SPF record does not specify if the source server is allowed to send email for the domain.")
  ])
], -1)
const _hoisted_22 = /*#__PURE__*/_createElementVNode("p", null, "Fail causes the email to be rejected. Softfail allows the email to be accepted, but it may be treated as suspicious. It might be placed into a spam folder or in quarantine.", -1)
const _hoisted_23 = /*#__PURE__*/_createElementVNode("p", null, "Neutral is not very useful by itself. But it can be used with DMARC (Domain-based Message Authentication, Reporting, and Conformance) to trigger special mail handling.", -1)
const _hoisted_24 = /*#__PURE__*/_createElementVNode("h2", null, "SPF mechanisms", -1)
const _hoisted_25 = /*#__PURE__*/_createElementVNode("p", null, "A mechanism identifies one or more mail servers. Mechanisms include:", -1)
const _hoisted_26 = /*#__PURE__*/_createElementVNode("ul", null, [
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("strong", null, "ip4"),
    /*#__PURE__*/_createTextVNode(": Specifies an IPv4 address or subnet.")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("strong", null, "ip6"),
    /*#__PURE__*/_createTextVNode(": Specifies an IPv6 address or subnet.")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("strong", null, "mx"),
    /*#__PURE__*/_createTextVNode(": Uses a type MX record lookup for the domain.")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("strong", null, "a"),
    /*#__PURE__*/_createTextVNode(": Uses a type A or AAAA lookup.")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("strong", null, "exists"),
    /*#__PURE__*/_createTextVNode(": Uses a type A lookup in a different way than the "),
    /*#__PURE__*/_createElementVNode("code", null, "a"),
    /*#__PURE__*/_createTextVNode(" mechanism.")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("strong", null, "include"),
    /*#__PURE__*/_createTextVNode(": Includes the SPF record for some other domain name.")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("strong", null, "all"),
    /*#__PURE__*/_createTextVNode(": This mechanism always appears at the end of the SPF record.")
  ])
], -1)
const _hoisted_27 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("If a mechanism is determined to be a match, the qualifier decides the email's fate. For example, if the qualifier is omitted or is "),
  /*#__PURE__*/_createElementVNode("code", null, "+"),
  /*#__PURE__*/_createTextVNode(", the SPF result is Pass and the email is delivered. If the qualifier is "),
  /*#__PURE__*/_createElementVNode("code", null, "-"),
  /*#__PURE__*/_createTextVNode(", the SPF result is Fail and the email is bounced.")
], -1)
const _hoisted_28 = /*#__PURE__*/_createElementVNode("h3", null, "The ip4 and ip6 mechanisms", -1)
const _hoisted_29 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("These mechanisms contain either a single IP address or a subnet. A subnet is a range of IP addresses that share a common prefix. For IPv4, the mechanism may specify a single address such as "),
  /*#__PURE__*/_createElementVNode("code", null, "ip4:10.0.0.1"),
  /*#__PURE__*/_createTextVNode(" or a subnet such as "),
  /*#__PURE__*/_createElementVNode("code", null, "ip4:10.0.0.0/24"),
  /*#__PURE__*/_createTextVNode(". For IPv6, the mechanism may specify a single address such as "),
  /*#__PURE__*/_createElementVNode("code", null, "ip6:2001:1000::1"),
  /*#__PURE__*/_createTextVNode(" or a subnet such as "),
  /*#__PURE__*/_createElementVNode("code", null, "ip6:2001:1000::/64"),
  /*#__PURE__*/_createTextVNode(".")
], -1)
const _hoisted_30 = /*#__PURE__*/_createElementVNode("p", null, "These mechanisms match if the mail server's IP address matches the single address or is within the subnet.", -1)
const _hoisted_31 = /*#__PURE__*/_createElementVNode("p", null, "This example allows an IPv4 subnet and single IPv6 address to send mail for the domain:", -1)
const _hoisted_32 = /*#__PURE__*/_createElementVNode("pre", null, "example.org. 3600 TXT \"v=spf1 ip4:10.0.0.0/24 ip6:2001:1000::1 -all\"", -1)
const _hoisted_33 = /*#__PURE__*/_createElementVNode("h3", null, "The mx mechanism", -1)
const _hoisted_34 = /*#__PURE__*/_createElementVNode("p", null, "This example omits the domain, so the MX record lookup would be for the domain from the Return-Path field:", -1)
const _hoisted_35 = /*#__PURE__*/_createElementVNode("pre", null, "example.org. 3600 TXT \"v=spf1 mx -all\"", -1)
const _hoisted_36 = /*#__PURE__*/_createElementVNode("p", null, "The domain can be specified explicitly, or another domain can be specified. For example:", -1)
const _hoisted_37 = /*#__PURE__*/_createElementVNode("pre", null, "example.org. 3600 TXT \"v=spf1 mx:examplemailfactory.org -all\"", -1)
const _hoisted_38 = /*#__PURE__*/_createElementVNode("h3", null, "The a mechanism", -1)
const _hoisted_39 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("The administrator could publish the mail server IP addresses for the domain at "),
  /*#__PURE__*/_createElementVNode("code", null, "mailservers.example.org"),
  /*#__PURE__*/_createTextVNode(" and use this SPF record:")
], -1)
const _hoisted_40 = /*#__PURE__*/_createElementVNode("pre", null, "example.org. 3600 TXT \"v=spf1 a:mailservers.example.org -all\"", -1)
const _hoisted_41 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("The domain may be omitted: "),
  /*#__PURE__*/_createElementVNode("code", null, "v=spf1 a -all"),
  /*#__PURE__*/_createTextVNode(". The type A or AAAA lookup would be for the current domain, which starts as the domain from the Return-Path field.")
], -1)
const _hoisted_42 = /*#__PURE__*/_createElementVNode("h3", null, "The exists mechanism", -1)
const _hoisted_43 = /*#__PURE__*/_createElementVNode("p", null, "This advanced mechanism is not often used in practice. Here is a trivial example:", -1)
const _hoisted_44 = /*#__PURE__*/_createElementVNode("pre", null, "example.org. 3600 TXT \"v=spf1 exists:some_name.example.org -all\"", -1)
const _hoisted_45 = /*#__PURE__*/_createElementVNode("code", null, "some_name.example.org", -1)
const _hoisted_46 = /*#__PURE__*/_createElementVNode("code", null, "exists", -1)
const _hoisted_47 = /*#__PURE__*/_createElementVNode("h3", null, "The include mechanism", -1)
const _hoisted_48 = /*#__PURE__*/_createElementVNode("p", null, "The include mechanism triggers evaluation of the SPF record for some other domain. Multiple includes are permitted. This mechanism is used when email for the domain is sent by a third-party mail provider. An SPF record can also be split into multiple parts using the include mechanism.", -1)
const _hoisted_49 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("This SPF would be used if the domain's mail provider has an SPF record at "),
  /*#__PURE__*/_createElementVNode("code", null, "examplemailfactory.org"),
  /*#__PURE__*/_createTextVNode(":")
], -1)
const _hoisted_50 = /*#__PURE__*/_createElementVNode("pre", null, "example.org. 3600 TXT \"v=spf1 include:examplemailfactory.org -all\"", -1)
const _hoisted_51 = /*#__PURE__*/_createElementVNode("h3", null, "The all mechanism", -1)
const _hoisted_52 = /*#__PURE__*/_createElementVNode("p", null, "The all mechanism appears at the end of all SPF records (unless the SPF uses the redirect modifier).", -1)
const _hoisted_53 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("The all mechanism specifies what should happen if none of the other mechanisms match. The all mechanism usually appears with the fail or softfail modifier. Softfail is more tolerant of errors in the SPF record, but for security it is best to use "),
  /*#__PURE__*/_createElementVNode("code", null, "-all"),
  /*#__PURE__*/_createTextVNode(".")
], -1)
const _hoisted_54 = /*#__PURE__*/_createElementVNode("h2", null, "SPF modifiers", -1)
const _hoisted_55 = /*#__PURE__*/_createElementVNode("p", null, "In addition to mechanisms, SPF also has something called modifiers. Modifiers look like mechanisms, but different rules apply. Modifiers are used infrequently. There are two modifiers: redirect and exp. Modifiers are always followed by an equals sign and then a value for the modifier.", -1)
const _hoisted_56 = /*#__PURE__*/_createElementVNode("h3", null, "The redirect modifier", -1)
const _hoisted_57 = /*#__PURE__*/_createElementVNode("p", null, "The redirect modifier looks similar to the include mechanism, but there are significant differences. Modifiers are processed only after all mechanisms have been evaluated, and no match has been found.", -1)
const _hoisted_58 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("If an SPF record contains the "),
  /*#__PURE__*/_createElementVNode("code", null, "all"),
  /*#__PURE__*/_createTextVNode(" mechanism, then at least one of the mechanisms (the "),
  /*#__PURE__*/_createElementVNode("code", null, "all"),
  /*#__PURE__*/_createTextVNode(" mechanism itself) will always match. So the "),
  /*#__PURE__*/_createElementVNode("code", null, "all"),
  /*#__PURE__*/_createTextVNode(" mechanism must be omitted when the SPF record uses the "),
  /*#__PURE__*/_createElementVNode("code", null, "redirect"),
  /*#__PURE__*/_createTextVNode(" modifier.")
], -1)
const _hoisted_59 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("Here is an example that redirects to a third-party email provider. Note there is no "),
  /*#__PURE__*/_createElementVNode("code", null, "all"),
  /*#__PURE__*/_createTextVNode(" mechanism:")
], -1)
const _hoisted_60 = /*#__PURE__*/_createElementVNode("pre", null, "example.org. 3600 TXT \"v=spf1 redirect=examplemailfactory.org\"", -1)
const _hoisted_61 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("Use of the "),
  /*#__PURE__*/_createElementVNode("code", null, "redirect"),
  /*#__PURE__*/_createTextVNode(" modifier is rare. The "),
  /*#__PURE__*/_createElementVNode("code", null, "include"),
  /*#__PURE__*/_createTextVNode(" mechanism is usually the right choice.")
], -1)
const _hoisted_62 = /*#__PURE__*/_createElementVNode("h3", null, "The exp modifier", -1)
const _hoisted_63 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("Exp is short for explanation. The "),
  /*#__PURE__*/_createElementVNode("code", null, "exp"),
  /*#__PURE__*/_createTextVNode(" modifier is triggered when the SPF check causes mail failure. The "),
  /*#__PURE__*/_createElementVNode("code", null, "exp"),
  /*#__PURE__*/_createTextVNode(" modifier gives a DNS name that the receiving mail server will use to retrieve a TXT record. The contents of the TXT result contain an explanation for the failure. SPF macros can be used in the message to provide detailed information.")
], -1)
const _hoisted_64 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("An example of the "),
  /*#__PURE__*/_createElementVNode("code", null, "exp"),
  /*#__PURE__*/_createTextVNode(" modifier:")
], -1)
const _hoisted_65 = /*#__PURE__*/_createElementVNode("pre", null, "example.org. 3600 TXT \"v=spf1 ip4:10.0.0.0/24 exp=explain.spf.example.org -all\"", -1)
const _hoisted_66 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("The domain would also contain a TXT containing the explanation message at "),
  /*#__PURE__*/_createElementVNode("code", null, "explain.spf.example.org"),
  /*#__PURE__*/_createTextVNode(":")
], -1)
const _hoisted_67 = /*#__PURE__*/_createElementVNode("pre", null, "explain.spf.example.org. 3600 TXT \"Mail for example.org may only be sent by authorized servers\"", -1)
const _hoisted_68 = /*#__PURE__*/_createElementVNode("h2", null, "SPF macros", -1)
const _hoisted_69 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("In addition to the features of SPF we've discussed, "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc7208#section-7",
    rel: "noopener noreferrer"
  }, "RFC 7208 section 7"),
  /*#__PURE__*/_createTextVNode(" also specifies "),
  /*#__PURE__*/_createElementVNode("strong", null, "macros"),
  /*#__PURE__*/_createTextVNode(".")
], -1)
const _hoisted_70 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("Macros add flexibility and power to SPF. Macros appear in the SPF record as a percent sign followed by a macro letter inside curly braces. There are a number of supported macro letters. One macro is "),
  /*#__PURE__*/_createElementVNode("code", null, "%{i}"),
  /*#__PURE__*/_createTextVNode(". This macro expands to the IP address of the sending mail server.")
], -1)
const _hoisted_71 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("The "),
  /*#__PURE__*/_createElementVNode("code", null, "%{i}"),
  /*#__PURE__*/_createTextVNode(" macro can be used with the exists mechanism. Type A records would be created with names corresponding to each mail server IP address. The addresses in these A records are not used. It is only important that these records exist.")
], -1)
const _hoisted_72 = /*#__PURE__*/_createElementVNode("pre", null, "10.0.0.1.spf.example.org. 3600 A 1.2.3.4\n10.0.0.2.spf.example.org. 3600 A 1.2.3.4", -1)
const _hoisted_73 = /*#__PURE__*/_createElementVNode("p", null, "With these A records for each of the mail server IP addresses, the SPF record would be:", -1)
const _hoisted_74 = /*#__PURE__*/_createElementVNode("pre", null, "example.org. 3600 TXT \"v=spf1 exists:%{i}.spf.example.org -all\"", -1)
const _hoisted_75 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "SPF records are stored in TXT records.")
], -1)
const _hoisted_76 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("For more details on SPF record type, see "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc7208#section-3.1",
    rel: "noopener noreferrer"
  }, "RFC 7208 section 3.1"),
  /*#__PURE__*/_createTextVNode(".")
], -1)
const _hoisted_77 = /*#__PURE__*/_createElementVNode("h2", null, "The importance of simplicity", -1)
const _hoisted_78 = /*#__PURE__*/_createElementVNode("p", null, "Email administrators try to make their SPF records as small and simple as possible. There are a few reasons for this:", -1)
const _hoisted_79 = /*#__PURE__*/_createElementVNode("li", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "Efficiency"),
  /*#__PURE__*/_createTextVNode(": A complex SPF record may require many DNS lookups and be slow to evaluate.")
], -1)
const _hoisted_80 = /*#__PURE__*/_createElementVNode("li", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "Complexity"),
  /*#__PURE__*/_createTextVNode(": A complex SPF record will be harder to get right. And harder to maintain as email servers change over time.")
], -1)
const _hoisted_81 = /*#__PURE__*/_createElementVNode("li", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "Processing limits"),
  /*#__PURE__*/_createTextVNode(": "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc7208#section-4.6.4",
    rel: "noopener noreferrer"
  }, "RFC 7208 section 4.6.4"),
  /*#__PURE__*/_createTextVNode(" places a limit of 10 DNS lookups on SPF evaluation. It also recommends a maximum processing time of 20 seconds. A complex SPF record may fail to evaluate within these limits. This will cause legitimate emails to be rejected or treated as spam.")
], -1)
const _hoisted_82 = /*#__PURE__*/_createElementVNode("strong", null, "DNS TXT size", -1)
const _hoisted_83 = /*#__PURE__*/_createElementVNode("h2", null, "Amazon as an example", -1)
const _hoisted_84 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("Large organizations like Amazon have many email servers. This may require large or complex SPF records. Let's examine Amazon's SPF records as they existed at the time this article was written. The SPF record for "),
  /*#__PURE__*/_createElementVNode("code", null, "amazon.com"),
  /*#__PURE__*/_createTextVNode(" is:")
], -1)
const _hoisted_85 = /*#__PURE__*/_createElementVNode("pre", null, "dig txt amazon.com +short\n\"v=spf1 include:spf1.amazon.com include:spf2.amazon.com include:amazonses.com -all\"", -1)
const _hoisted_86 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("Amazon has broken its SPF into three parts and used the "),
  /*#__PURE__*/_createElementVNode("code", null, "include"),
  /*#__PURE__*/_createTextVNode(" mechanism for each part. This keeps each TXT record relatively small in size. It may also allow the parts to be re-used individually by other domains using the "),
  /*#__PURE__*/_createElementVNode("code", null, "include"),
  /*#__PURE__*/_createTextVNode(" mechanism. They have chosen the more secure "),
  /*#__PURE__*/_createElementVNode("code", null, "-all"),
  /*#__PURE__*/_createTextVNode(" over the less secure "),
  /*#__PURE__*/_createElementVNode("code", null, "~all"),
  /*#__PURE__*/_createTextVNode(".")
], -1)
const _hoisted_87 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("The three separate parts use the "),
  /*#__PURE__*/_createElementVNode("code", null, "ip4"),
  /*#__PURE__*/_createTextVNode(" mechanism with subnets to identify the IP addresses of Amazon's mail servers:")
], -1)
const _hoisted_88 = /*#__PURE__*/_createElementVNode("pre", null, "dig txt +short spf1.amazon.com\n\"v=spf1 ip4:207.171.160.0/19 ip4:87.238.80.0/21 ip4:72.21.192.0/19 ip4:194.154.193.192/27 ip4:194.7.41.152/28 ip4:212.123.28.40/32 ip4:203.81.17.0/24 ip4:178.236.10.128/26 ip4:52.94.124.0/28 ip4:99.78.197.208/28 ip4:52.119.213.144/28 -all\"\n\ndig txt +short spf2.amazon.com\n\"v=spf1 ip4:176.32.105.0/24 ip4:176.32.127.0/24 ip4:106.50.16.0/28 ip4:205.251.233.32/32 ip4:205.251.233.36/32 ip4:72.21.217.142/32 ip4:52.95.48.152/29 ip4:52.95.49.88/29 -all\"\n\ndig txt +short amazonses.com\n\"v=spf1 ip4:199.255.192.0/22 ip4:199.127.232.0/22 ip4:54.240.0.0/18 ip4:69.169.224.0/20 ip4:23.249.208.0/20 ip4:23.251.224.0/19 ip4:76.223.176.0/20 ip4:54.240.64.0/19 ip4:54.240.96.0/19 ip4:52.82.172.0/22 -all\"", -1)
const _hoisted_89 = /*#__PURE__*/_createElementVNode("p", null, "This SPF is large. But for an organization with a global footprint like Amazon this can't be helped. Amazon engineers likely work hard to keep the SPF as small as they can, given their network architecture.", -1)
const _hoisted_90 = /*#__PURE__*/_createElementVNode("h2", null, "Finding the SPF record for a domain", -1)
const _hoisted_91 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("SPF records are stored as TXT records, so a normal TXT lookup will retrieve SPF records. The dig or nslookup command line tools can be used. To find the SPF record for Google, use this command and look for the TXT record that begins with "),
  /*#__PURE__*/_createElementVNode("code", null, "v=spf1"),
  /*#__PURE__*/_createTextVNode(":")
], -1)
const _hoisted_92 = /*#__PURE__*/_createElementVNode("pre", null, "dig TXT google.com", -1)
const _hoisted_93 = /*#__PURE__*/_createElementVNode("p", null, "On operating systems that support nslookup, you can use the following:", -1)
const _hoisted_94 = /*#__PURE__*/_createElementVNode("pre", null, "nslookup -type=TXT google.com", -1)
const _hoisted_95 = {
  class: "not-prose text-base mx-auto max-w-lg",
  autocomplete: "off"
}
const _hoisted_96 = /*#__PURE__*/_createElementVNode("h2", null, "You may also need DMARC and DKIM", -1)
const _hoisted_97 = /*#__PURE__*/_createElementVNode("p", null, "SPF is only part of the story for securing email. There are three pillars of email security:", -1)
const _hoisted_98 = /*#__PURE__*/_createElementVNode("li", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "Sender Policy Framework (SPF)"),
  /*#__PURE__*/_createTextVNode(": SPF ensures that only authorized mail servers can send mail for a domain.")
], -1)
const _hoisted_99 = /*#__PURE__*/_createElementVNode("p", null, "While adding SPF to your domain is a good start, consider adding DKIM and DMARC to protect your domain and brand from abuse.", -1)
const _hoisted_100 = { class: "mx-auto max-w-3xl divide-y-2 divide-gray-200" }
const _hoisted_101 = /*#__PURE__*/_createElementVNode("h2", { class: "ignore-toc" }, "Related questions", -1)
const _hoisted_102 = { class: "mt-6 space-y-6 divide-y divide-gray-200 [overflow-anchor:none]" }
const _hoisted_103 = /*#__PURE__*/_createElementVNode("span", { class: "text-lg font-medium text-gray-900" }, " Why doesn't SPF validate the From address? ", -1)
const _hoisted_104 = { class: "ml-6 flex h-7 items-center" }
const _hoisted_105 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "Like many Internet protocols that have been around for a long time, the SMTP protocol has some quirks and inconsistencies.")
], -1)
const _hoisted_106 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("In "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc822",
    rel: "noopener noreferrer"
  }, "RFC 822"),
  /*#__PURE__*/_createTextVNode(", Return-Path was intended only to be set when the message is about to be delivered and placed in the recipient's inbox. This is when the SPF check is applied.")
], -1)
const _hoisted_107 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("However, "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc2821",
    rel: "noopener noreferrer"
  }, "RFC 2821"),
  /*#__PURE__*/_createTextVNode(" states: \"A message-originating SMTP system SHOULD NOT send a message that already contains a Return-Path header.\" This created a loophole for spammers to send messages that contain both From and Return-Path. It may have been necessary at the time to allow existing implementations to work properly.")
], -1)
const _hoisted_108 = /*#__PURE__*/_createElementVNode("span", { class: "text-lg font-medium text-gray-900" }, " Will the email sender get a bounce message if it fails SPF? ", -1)
const _hoisted_109 = { class: "ml-6 flex h-7 items-center" }
const _hoisted_110 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", { class: "font-medium text-gray-500" }, "Yes. Most mail servers will send a \"550 SPF Check Failed\" bounce message back to the sender.")
], -1)
const _hoisted_111 = /*#__PURE__*/_createElementVNode("span", { class: "text-lg font-medium text-gray-900" }, " Why do I still need SPF when DKIM exists? ", -1)
const _hoisted_112 = { class: "ml-6 flex h-7 items-center" }
const _hoisted_113 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", { class: "font-medium text-gray-500" }, "SPF and DKIM solve different security problems.")
], -1)
const _hoisted_114 = /*#__PURE__*/_createElementVNode("p", null, "SPF ensures that only authorized mail servers can send mail for a domain but this does not protect the message from tampering. DKIM adds a digital signature to protect against tampering.", -1)
const _hoisted_115 = /*#__PURE__*/_createElementVNode("span", { class: "text-lg font-medium text-gray-900" }, " Should I use ~all or -all? ", -1)
const _hoisted_116 = { class: "ml-6 flex h-7 items-center" }
const _hoisted_117 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", { class: "font-medium text-gray-500" }, "Both are acceptable but -all is more secure.")
], -1)
const _hoisted_118 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("Using "),
  /*#__PURE__*/_createElementVNode("code", null, "~all"),
  /*#__PURE__*/_createTextVNode(" results in a soft failure when there is no match for the mail server's IP address. Email may be flagged as suspicious or placed in a spam folder. This is good if the email is legitimate but there is an error in the SPF record. However, this means that malicious email may also be delivered and could reach your customers.")
], -1)
const _hoisted_119 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("It is best to use "),
  /*#__PURE__*/_createElementVNode("code", null, "-all"),
  /*#__PURE__*/_createTextVNode(" and be careful that your SPF record is correct.")
], -1)

import BlogPost from '@/components/layout/BlogPost.vue';


import { useRouter } from 'vue-router';
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/vue';
import { ChevronDownIcon } from '@heroicons/vue/24/outline';
import { jeff, ruurtjan } from '@/common/authors';
import { BlogMeta } from '@/common/blogMeta';
import DomainSearch from '@/components/DomainSearch.vue';
import CodeBlock from '@/components/micro/CodeBlock.vue';
import { learningBreadcrumb, emailBreadcrumb } from '@/model/Breadcrumb';
import Callout from '@/components/layout/CalloutComponent.vue';
import { domainNameToUrlPath } from '@/common/domainName';

export const meta: BlogMeta = {
  tag: 'Email and the DNS',
  url: '/learning/spf-a-practical-guide/',
  breadcrumbs: [learningBreadcrumb, emailBreadcrumb],
  title: 'SPF: A practical guide',
  description: 'Sender Policy Framework (SPF) records in the DNS identify the mail servers allowed to send email for a domain. SPF protects domains from email abuse.',
  overviewDescription: 'Sender Policy Framework (SPF) records in the DNS identify the mail servers allowed to send email for a domain. SPF protects domains from email abuse.',
  overviewImage: '/img/learning/spf-a-practical-guide/the-dns-spf-record-type-small.jpg',
  overviewImageAlt: 'SPF in the DNS',
  author: jeff,
  editor: ruurtjan,
  img1024x512: 'https://www.nslookup.io/img/learning/spf-a-practical-guide/the-spf-record-social.jpg',
  img1200x630: 'https://www.nslookup.io/img/learning/spf-a-practical-guide/the-spf-record-social.jpg',
  written: new Date('2022-09-09T09:23:58.118Z'),
  updated: new Date('2023-03-28T11:59:26.073Z'),
};

const __default__ = {
  meta,
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'EmailSecuritySpf',
  setup(__props) {

const router = useRouter();

function redirectTo(domain: string): void {
  router.push(`/domains/${domainNameToUrlPath(domain)}/email/spf/`);
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_openBlock(), _createBlock(_Teleport, { to: "head" }, [
      (_openBlock(), _createBlock(_resolveDynamicComponent('script'), { type: "application/ld+json" }, {
        default: _withCtx(() => [
          _createTextVNode(" { \"@context\": \"https://schema.org\", \"@type\": \"ImageObject\", \"author\": { \"@type\": \"Person\", \"email\": \"mailto:ruurtjan@nslookup.io\", \"image\": \"https://www.nslookup.io/img/ruurtjan-lg.jpg\", \"jobTitle\": \"CEO\", \"name\": \"Ruurtjan Pul\", \"url\": \"https://www.nslookup.io/about/ruurtjan-pul/\" }, \"copyrightHolder\": { \"@type\": \"Organization\", \"url\": \"https://www.nslookup.io/\", \"email\": \"hello@nslookup.io\" }, \"copyrightYear\": 2023, \"contentUrl\": \"https://www.nslookup.io/img/learning/spf-a-practical-guide/the-dns-spf-record-type.jpg\", \"license\": \"https://creativecommons.org/licenses/by/4.0/\", \"acquireLicensePage\": \"https://creativecommons.org/licenses/by/4.0/\" } ")
        ]),
        _: 1
      }))
    ])),
    _createVNode(BlogPost, {
      title: meta.title,
      description: meta.description,
      url: `https://www.nslookup.io${meta.url}`,
      breadcrumbs: meta.breadcrumbs,
      img1024x512: meta.img1024x512,
      img1200x630: meta.img1200x630,
      written: meta.written,
      updated: meta.updated,
      author: meta.author,
      editor: meta.editor
    }, {
      default: _withCtx(() => [
        _hoisted_1,
        _hoisted_2,
        _hoisted_3,
        _hoisted_4,
        _hoisted_5,
        _hoisted_6,
        _createElementVNode("p", null, [
          _createTextVNode("SPF records appear in the DNS as "),
          _createVNode(_component_router_link, { to: "/learning/dns-record-types/txt/" }, {
            default: _withCtx(() => [
              _createTextVNode("TXT records")
            ]),
            _: 1
          }),
          _createTextVNode(". The TXT record contains text that follows the SPF format. This record identifies the mail servers allowed to send email for the domain.")
        ]),
        _hoisted_7,
        _hoisted_8,
        _createVNode(CodeBlock, null, {
          default: _withCtx(() => [
            _hoisted_9
          ]),
          _: 1
        }),
        _createVNode(Callout, {
          icon: "info",
          title: "Return-Path versus From",
          heading: "h2"
        }, {
          default: _withCtx(() => [
            _hoisted_10,
            _hoisted_11,
            _hoisted_12,
            _hoisted_13
          ]),
          _: 1
        }),
        _hoisted_14,
        _hoisted_15,
        _hoisted_16,
        _hoisted_17,
        _hoisted_18,
        _hoisted_19,
        _hoisted_20,
        _hoisted_21,
        _hoisted_22,
        _hoisted_23,
        _hoisted_24,
        _hoisted_25,
        _hoisted_26,
        _hoisted_27,
        _hoisted_28,
        _hoisted_29,
        _hoisted_30,
        _hoisted_31,
        _createVNode(CodeBlock, null, {
          default: _withCtx(() => [
            _hoisted_32
          ]),
          _: 1
        }),
        _hoisted_33,
        _createElementVNode("p", null, [
          _createTextVNode("This mechanism triggers a type "),
          _createVNode(_component_router_link, { to: "/learning/dns-record-types/mx/" }, {
            default: _withCtx(() => [
              _createTextVNode("MX")
            ]),
            _: 1
          }),
          _createTextVNode(" lookup for the domain. A type "),
          _createVNode(_component_router_link, { to: "/learning/dns-record-types/a/" }, {
            default: _withCtx(() => [
              _createTextVNode("A")
            ]),
            _: 1
          }),
          _createTextVNode(" or "),
          _createVNode(_component_router_link, { to: "/learning/dns-record-types/aaaa/" }, {
            default: _withCtx(() => [
              _createTextVNode("AAAA")
            ]),
            _: 1
          }),
          _createTextVNode(" lookup is performed for each server in the MX record set to arrive at a set of IP addresses. This mechanism matches if the results contain the source mail server's IP address.")
        ]),
        _hoisted_34,
        _createVNode(CodeBlock, null, {
          default: _withCtx(() => [
            _hoisted_35
          ]),
          _: 1
        }),
        _hoisted_36,
        _createVNode(CodeBlock, null, {
          default: _withCtx(() => [
            _hoisted_37
          ]),
          _: 1
        }),
        _hoisted_38,
        _createElementVNode("p", null, [
          _createTextVNode("This mechanism specifies a domain name. The receiving mail server performs a type "),
          _createVNode(_component_router_link, { to: "/learning/dns-record-types/a/" }, {
            default: _withCtx(() => [
              _createTextVNode("A")
            ]),
            _: 1
          }),
          _createTextVNode(" or "),
          _createVNode(_component_router_link, { to: "/learning/dns-record-types/aaaa/" }, {
            default: _withCtx(() => [
              _createTextVNode("AAAA")
            ]),
            _: 1
          }),
          _createTextVNode(" lookup for the specified name. This mechanism is a match if the IP address of the sending mail server is found in the result.")
        ]),
        _hoisted_39,
        _createVNode(CodeBlock, null, {
          default: _withCtx(() => [
            _hoisted_40
          ]),
          _: 1
        }),
        _hoisted_41,
        _hoisted_42,
        _hoisted_43,
        _createVNode(CodeBlock, null, {
          default: _withCtx(() => [
            _hoisted_44
          ]),
          _: 1
        }),
        _createElementVNode("p", null, [
          _createTextVNode("This SPF record would trigger a type "),
          _createVNode(_component_router_link, { to: "/learning/dns-record-types/a/" }, {
            default: _withCtx(() => [
              _createTextVNode("A")
            ]),
            _: 1
          }),
          _createTextVNode(" lookup for "),
          _hoisted_45,
          _createTextVNode(". If any type A record is returned, the mechanism is a match. This example is not useful but the "),
          _hoisted_46,
          _createTextVNode(" mechanism can be combined with SPF macros. We'll discuss macros later.")
        ]),
        _hoisted_47,
        _hoisted_48,
        _hoisted_49,
        _createVNode(CodeBlock, null, {
          default: _withCtx(() => [
            _hoisted_50
          ]),
          _: 1
        }),
        _hoisted_51,
        _hoisted_52,
        _hoisted_53,
        _hoisted_54,
        _hoisted_55,
        _hoisted_56,
        _hoisted_57,
        _hoisted_58,
        _hoisted_59,
        _createVNode(CodeBlock, null, {
          default: _withCtx(() => [
            _hoisted_60
          ]),
          _: 1
        }),
        _hoisted_61,
        _hoisted_62,
        _hoisted_63,
        _hoisted_64,
        _createVNode(CodeBlock, null, {
          default: _withCtx(() => [
            _hoisted_65
          ]),
          _: 1
        }),
        _hoisted_66,
        _createVNode(CodeBlock, null, {
          default: _withCtx(() => [
            _hoisted_67
          ]),
          _: 1
        }),
        _hoisted_68,
        _hoisted_69,
        _hoisted_70,
        _hoisted_71,
        _createVNode(CodeBlock, null, {
          default: _withCtx(() => [
            _hoisted_72
          ]),
          _: 1
        }),
        _hoisted_73,
        _createVNode(CodeBlock, null, {
          default: _withCtx(() => [
            _hoisted_74
          ]),
          _: 1
        }),
        _createVNode(Callout, {
          icon: "info",
          title: "What record type for SPF?",
          heading: "h2"
        }, {
          default: _withCtx(() => [
            _hoisted_75,
            _createElementVNode("p", null, [
              _createTextVNode("Initially, the creators of SPF wanted to use a new "),
              _createVNode(_component_router_link, { to: "/learning/dns-record-types/" }, {
                default: _withCtx(() => [
                  _createTextVNode("DNS record type")
                ]),
                _: 1
              }),
              _createTextVNode(". The numeric value of this type was 99. Unfortunately, it proved too challenging to add a new record type to the global DNS. So SPF records are stored using the TXT record type.")
            ]),
            _hoisted_76
          ]),
          _: 1
        }),
        _hoisted_77,
        _hoisted_78,
        _createElementVNode("ul", null, [
          _hoisted_79,
          _hoisted_80,
          _hoisted_81,
          _createElementVNode("li", null, [
            _hoisted_82,
            _createTextVNode(": DNS TXT record strings are "),
            _createVNode(_component_router_link, { to: "/learning/dns-record-types/txt/#txt-record-format" }, {
              default: _withCtx(() => [
                _createTextVNode("limited to 255 characters")
              ]),
              _: 1
            }),
            _createTextVNode(". Larger SPF records are supported, but TXT at the root of the domain is shared with domain authorization and other uses. Overly large TXT record sets may introduce TCP DNS lookups, which should be avoided.")
          ])
        ]),
        _hoisted_83,
        _hoisted_84,
        _hoisted_85,
        _hoisted_86,
        _hoisted_87,
        _hoisted_88,
        _hoisted_89,
        _hoisted_90,
        _hoisted_91,
        _hoisted_92,
        _hoisted_93,
        _hoisted_94,
        _createElementVNode("p", null, [
          _createTextVNode("You can also check the SPF records for any domain name in our "),
          _createVNode(_component_router_link, { to: "/spf-lookup/" }, {
            default: _withCtx(() => [
              _createTextVNode("SPF lookup")
            ]),
            _: 1
          }),
          _createTextVNode(" tool or by entering it here:")
        ]),
        _createElementVNode("form", _hoisted_95, [
          _createVNode(DomainSearch, {
            emphasize: "",
            buttonText: "Find SPF record",
            onSubmitted: redirectTo
          })
        ]),
        _hoisted_96,
        _hoisted_97,
        _createElementVNode("ul", null, [
          _hoisted_98,
          _createElementVNode("li", null, [
            _createElementVNode("strong", null, [
              _createVNode(_component_router_link, { to: "/learning/dkim-a-practical-guide/" }, {
                default: _withCtx(() => [
                  _createTextVNode("Domain-keys Identified Mail (DKIM)")
                ]),
                _: 1
              })
            ]),
            _createTextVNode(": DKIM uses public key cryptography to validate the sender's authenticity and to protect email messages from tampering.")
          ]),
          _createElementVNode("li", null, [
            _createElementVNode("strong", null, [
              _createVNode(_component_router_link, { to: "/learning/dmarc-a-practical-guide/" }, {
                default: _withCtx(() => [
                  _createTextVNode("Domain-based Message Authentication, Reporting, and Conformance (DMARC)")
                ]),
                _: 1
              })
            ]),
            _createTextVNode(": DMARC adds policy and reporting. It prevents spammers from abusing the Return-Path field to evade SPF. DMARC also allows administrators to control how messages that fail SPF checks should be treated.")
          ])
        ]),
        _hoisted_99,
        _createElementVNode("p", null, [
          _createTextVNode("Read my next article to learn "),
          _createVNode(_component_router_link, { to: "/learning/mx-vs-spf-vs-dmarc-vs-dkim-vs-bimi/" }, {
            default: _withCtx(() => [
              _createTextVNode("the difference between MX, SPF, DKIM, DMARC and BIMI")
            ]),
            _: 1
          }),
          _createTextVNode(".")
        ]),
        _createElementVNode("div", _hoisted_100, [
          _hoisted_101,
          _createElementVNode("div", _hoisted_102, [
            _createVNode(_unref(Disclosure), {
              as: "div",
              class: "pt-6"
            }, {
              default: _withCtx(({ open }) => [
                _createVNode(_unref(DisclosureButton), {
                  as: "h3",
                  class: "ignore-toc flex mt-0 w-full items-start justify-between text-left cursor-pointer text-gray-400"
                }, {
                  default: _withCtx(() => [
                    _hoisted_103,
                    _createElementVNode("span", _hoisted_104, [
                      _createVNode(_unref(ChevronDownIcon), {
                        class: _normalizeClass([open ? '-rotate-180' : 'rotate-0', 'h-6 w-6 transform']),
                        "aria-hidden": "true"
                      }, null, 8, ["class"])
                    ])
                  ]),
                  _: 2
                }, 1024),
                _createVNode(_unref(DisclosurePanel), { class: "mt-2 pr-12" }, {
                  default: _withCtx(() => [
                    _hoisted_105,
                    _hoisted_106,
                    _hoisted_107
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_unref(Disclosure), {
              as: "div",
              class: "pt-6"
            }, {
              default: _withCtx(({ open }) => [
                _createVNode(_unref(DisclosureButton), {
                  as: "h3",
                  class: "ignore-toc flex mt-0 w-full items-start justify-between text-left cursor-pointer text-gray-400"
                }, {
                  default: _withCtx(() => [
                    _hoisted_108,
                    _createElementVNode("span", _hoisted_109, [
                      _createVNode(_unref(ChevronDownIcon), {
                        class: _normalizeClass([open ? '-rotate-180' : 'rotate-0', 'h-6 w-6 transform']),
                        "aria-hidden": "true"
                      }, null, 8, ["class"])
                    ])
                  ]),
                  _: 2
                }, 1024),
                _createVNode(_unref(DisclosurePanel), { class: "mt-2 pr-12" }, {
                  default: _withCtx(() => [
                    _hoisted_110
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_unref(Disclosure), {
              as: "div",
              class: "pt-6"
            }, {
              default: _withCtx(({ open }) => [
                _createVNode(_unref(DisclosureButton), {
                  as: "h3",
                  class: "ignore-toc flex mt-0 w-full items-start justify-between text-left cursor-pointer text-gray-400"
                }, {
                  default: _withCtx(() => [
                    _hoisted_111,
                    _createElementVNode("span", _hoisted_112, [
                      _createVNode(_unref(ChevronDownIcon), {
                        class: _normalizeClass([open ? '-rotate-180' : 'rotate-0', 'h-6 w-6 transform']),
                        "aria-hidden": "true"
                      }, null, 8, ["class"])
                    ])
                  ]),
                  _: 2
                }, 1024),
                _createVNode(_unref(DisclosurePanel), { class: "mt-2 pr-12" }, {
                  default: _withCtx(() => [
                    _hoisted_113,
                    _hoisted_114
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_unref(Disclosure), {
              as: "div",
              class: "pt-6"
            }, {
              default: _withCtx(({ open }) => [
                _createVNode(_unref(DisclosureButton), {
                  as: "h3",
                  class: "ignore-toc flex mt-0 w-full items-start justify-between text-left cursor-pointer text-gray-400"
                }, {
                  default: _withCtx(() => [
                    _hoisted_115,
                    _createElementVNode("span", _hoisted_116, [
                      _createVNode(_unref(ChevronDownIcon), {
                        class: _normalizeClass([open ? '-rotate-180' : 'rotate-0', 'h-6 w-6 transform']),
                        "aria-hidden": "true"
                      }, null, 8, ["class"])
                    ])
                  ]),
                  _: 2
                }, 1024),
                _createVNode(_unref(DisclosurePanel), { class: "mt-2 pr-12" }, {
                  default: _withCtx(() => [
                    _hoisted_117,
                    _hoisted_118,
                    _hoisted_119
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ])
        ])
      ]),
      _: 1
    }, 8, ["title", "description", "url", "breadcrumbs", "img1024x512", "img1200x630", "written", "updated", "author", "editor"])
  ], 64))
}
}

})